import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useQuota } from "@/hooks/useQuota";
import type { Capability } from "@/lib/quotas";

/**
 * Detects whether an error coming back from a server function is a 402-quota
 * error. Server functions throw a `Response`/string with the QuotaExceededError
 * message ("You've hit your monthly … limit"); the SDK surfaces this as an
 * Error whose `.message` contains the same prefix.
 */
export function isQuotaError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return /you'?ve hit your monthly/i.test(msg) || /quota exceeded/i.test(msg);
}

/**
 * Per-capability quota dialog controller.
 *
 * Usage:
 *   const quotaDialog = useQuotaError(CAPABILITIES.resume_generation);
 *   try { ... } catch (e) { if (!quotaDialog.handle(e)) toast.error(...); }
 *   <QuotaUpgradeDialog open={quotaDialog.open} onClose={quotaDialog.close} quota={quotaDialog.quota} />
 */
export function useQuotaError(capability: Capability) {
  const quota = useQuota(capability);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);

  const handle = useCallback(
    (err: unknown): boolean => {
      if (!isQuotaError(err)) return false;
      setMessage(err instanceof Error ? err.message : String(err));
      setOpen(true);
      // Refresh usage so the dialog shows the freshest counts.
      void queryClient.invalidateQueries({ queryKey: ["usage-summary"] });
      return true;
    },
    [queryClient],
  );

  const close = useCallback(() => setOpen(false), []);

  return { quota, open, message, handle, close };
}
